import {
	ATTRIBUTION_PARAMS,
	EXCLUDED_FROM_COMPARISON,
} from "@shared/constants";
import hasAttributionChanged from "./hasAttributionChanged";

/**
 * Checks if only the click ID has changed between two URLs while all other attribution parameters remain the same
 * @param currentUrl - The current URL
 * @param previousUrl - The previous/ingress URL
 * @returns true if only the click ID has changed (attribution unchanged but click ID different), false otherwise
 */
export default function hasClickIdOnlyChanged(
	currentUrl: string,
	previousUrl: string,
	attributionChangedStatus = false,
): boolean {
	if (!currentUrl || !previousUrl || attributionChangedStatus) {
		return false;
	}

	// First check if attribution has changed (excluding click IDs)
	// If attribution changed, then it's not "only" the click ID that changed
	if (hasAttributionChanged(currentUrl, previousUrl)) {
		return false;
	}

	// Attribution hasn't changed - now check if click IDs are different
	try {
		const currentUrlObj = new URL(currentUrl);
		const previousUrlObj = new URL(previousUrl);

		const currentParams = currentUrlObj.searchParams;
		const previousParams = previousUrlObj.searchParams;

		// Extract click IDs from both URLs
		const currentClickIds = new Map<string, string>();
		const previousClickIds = new Map<string, string>();

		// Process current URL params for click IDs
		for (const [key, value] of currentParams.entries()) {
			const lowerKey = key.toLowerCase();
			if (
				ATTRIBUTION_PARAMS.has(lowerKey) &&
				EXCLUDED_FROM_COMPARISON.has(lowerKey)
			) {
				currentClickIds.set(lowerKey, value);
			}
		}

		// Process previous URL params for click IDs
		for (const [key, value] of previousParams.entries()) {
			const lowerKey = key.toLowerCase();
			if (
				ATTRIBUTION_PARAMS.has(lowerKey) &&
				EXCLUDED_FROM_COMPARISON.has(lowerKey)
			) {
				previousClickIds.set(lowerKey, value);
			}
		}

		// If neither URL has click IDs, nothing changed
		if (currentClickIds.size === 0 && previousClickIds.size === 0) {
			return false;
		}

		// Check if click IDs differ (either different keys or different values)
		const allClickIdKeys = new Set([
			...currentClickIds.keys(),
			...previousClickIds.keys(),
		]);

		// If the set of click ID keys differs, it's a change
		if (
			currentClickIds.size !== allClickIdKeys.size ||
			previousClickIds.size !== allClickIdKeys.size
		) {
			return true;
		}

		// Same keys exist - check if any values differ
		for (const [key, value] of currentClickIds.entries()) {
			if (previousClickIds.get(key) !== value) {
				return true;
			}
		}

		// Click IDs are the same (or don't exist), so nothing changed
		return false;
	} catch {
		// Invalid URL
		return false;
	}
}
